import { HUB_LANDING_PAGES } from './hubLanding';
import { ROUTE_LANDING_PAGES } from './routeLanding';
import { FEATURED_ROUTE_LANDING_PAGES } from './featuredRoutes';
import { LOCAL_CORRIDOR_PAGES } from './localCorridorPages';

/**
 * The 16 Tamil Nadu districts from the 31 Oct 2026 sitemap audit.
 * Names are the audit strings. Do not add a district that already has a hub or route URL.
 */
export const DISTRICT_AUDIT_NAMES = [
  'Ariyalur',
  'Chengalpattu',
  'Dharmapuri',
  'Dindigul',
  'Erode',
  'Kallakurichi',
  'Kancheepuram',
  'Mayiladuthurai',
  'Namakkal',
  'Perambalur',
  'Pudukkottai',
  'Ranipet',
  'Sivaganga',
  'Theni',
  'Tiruvallur',
  'Virudhunagar',
];

const CORE_PATHS = new Set(['/', '/tariff', '/taxi-routes', '/services', '/contact']);

const RESERVED_PATHS = new Set([
  ...HUB_LANDING_PAGES.map((page) => page.slug),
  ...ROUTE_LANDING_PAGES.map((page) => page.slug),
  ...FEATURED_ROUTE_LANDING_PAGES.map((page) => page.slug),
  ...LOCAL_CORRIDOR_PAGES.map((page) => page.slug),
  '/singanallur-taxi',
  '/chennai-to-madurai-taxi',
  '/madurai-to-chennai-taxi',
  '/coimbatore-to-ooty',
  '/taxi-routes',
]);

const districtSlug = (name) => `/${name.toLowerCase()}-taxi`;

const SOURCE = [
  {
    name: 'Ariyalur',
    heroLead:
      'Book a one-way taxi from Ariyalur when the trip starts in this central Tamil Nadu district and the drop is a city you choose.',
    seoDescription:
      'Book a one-way taxi from Ariyalur with Easy Drop Taxi. Pickup starts in Ariyalur district, you choose the drop, and the fare uses the same live estimate as the homepage form.',
    intro: [
      'Ariyalur district sits between Tiruchirappalli and the Cauvery delta. The district headquarters is Ariyalur town, and Jayankondam is the other large town travellers usually name. This page is the Ariyalur district landing. It does not replace the Trichy hub.',
      'Easy Drop Taxi does not yet publish an Ariyalur-to-city route URL. Use this page when pickup is in Ariyalur district, enter the drop yourself, and estimate the fare in the same booking card used on the homepage.',
      'One Way is the default so the cab is not held in Ariyalur for a return. Switch to Round Trip in the booking card only when both legs are required.',
    ],
    placesNote:
      'Cement plants and the fossil beds around Ariyalur are local landmarks, not separate taxi pages. If the pickup is a village or a plant gate, type that address instead of leaving the field as Ariyalur town.',
    serviceNote:
      'Ariyalur is the district name prefilled here. Tiruchirappalli already has /trichy-taxi. Open that hub when the trip actually starts in Trichy.',
    benefits: [
      {
        title: 'District pickup, not a Trichy booking',
        text: 'Keep Ariyalur when the boarding point is in Ariyalur district. Change it only if the traveller is waiting in another town.',
      },
      {
        title: 'No second Ariyalur URL',
        text: 'This /ariyalur-taxi page is the only Ariyalur landing. Pair pages are not invented for drops that do not already exist.',
      },
      {
        title: 'Delta or Trichy drops',
        text: 'Thanjavur, Trichy and Chennai can be typed as the drop. A fare appears only after the form maps that trip.',
      },
      {
        title: 'Same booking card',
        text: 'Vehicle, date, time and confirmation stay on the Easy Drop Taxi form. This page does not add a second booking system.',
      },
    ],
    howTo: [
      'Keep or edit Ariyalur as pickup, enter the drop town or address, add a 10-digit mobile number, then tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta, then add the date and time you want to leave Ariyalur and your name.',
      'Review the mapped trip and fare, then confirm. The request uses the existing Easy Drop Taxi booking system.',
    ],
    faqs: [
      {
        question: 'How do I book a taxi from Ariyalur?',
        answer:
          'Pickup on this page starts as Ariyalur. Enter the drop, such as Trichy, Thanjavur or Chennai, add your mobile number, and tap Get Fare Estimate. Choose Sedan, SUV or Crysta with the leave date and time, then confirm the review.',
      },
      {
        question: 'Is Ariyalur the same page as Trichy Taxi?',
        answer:
          'No. Trichy Taxi at /trichy-taxi is the Tiruchirappalli hub. Use this Ariyalur page only when the pickup is in Ariyalur district.',
      },
      {
        question: 'Does Easy Drop Taxi publish an Ariyalur kilometre package?',
        answer:
          'No. This page does not print a district package fare. The booking card maps the pickup and drop you enter and applies the same one-way tariff used everywhere else on the site.',
      },
      {
        question: 'Can I start from Jayankondam instead of Ariyalur town?',
        answer:
          'Yes. Replace the pickup with Jayankondam or the exact boarding address before you estimate. Jayankondam stays inside Ariyalur district and does not get a separate URL.',
      },
    ],
    sampleDrops: ['Trichy', 'Thanjavur', 'Chennai'],
    related: [
      { path: '/trichy-taxi', label: 'Trichy Taxi' },
      { path: '/coimbatore-to-thanjavur-taxi', label: 'Coimbatore to Thanjavur Taxi' },
      { path: '/tariff', label: 'Tariff' },
      { path: '/', label: 'Home' },
    ],
  },
  {
    name: 'Chengalpattu',
    heroLead:
      'Leave Chengalpattu district in a one-way cab when the trip starts south of Chennai and you still need to name the drop.',
    seoDescription:
      'Book a one-way taxi from Chengalpattu district with Easy Drop Taxi. This is the Chengalpattu page, separate from the Chennai hub and from the Chengalpet spelling in the booking list.',
    intro: [
      'Chengalpattu is the district immediately south of Chennai, along the GST corridor and the East Coast. The headquarters is Chengalpattu. Mahabalipuram, also called Mamallapuram, is in this district. This landing uses the audit spelling Chengalpattu.',
      'The homepage booking list spells a town Chengalpet. That label is not a second page. Do not look for /chengalpet-taxi. Travellers who search either spelling book from this Chengalpattu URL, then edit pickup if the boarding point is a specific town.',
      'Chennai already has /chennai-taxi. Use the Chennai hub when pickup is in Chennai. Use this page when the cab must start in Chengalpattu district.',
    ],
    placesNote:
      'Mahabalipuram, Singaperumal Koil and the GST towns toward Tindivanam are places inside the district, not extra district pages. Type the actual boarding point if it is not Chengalpattu town.',
    serviceNote:
      'Kancheepuram is a neighbouring district with its own page at /kancheepuram-taxi. A Chengalpattu pickup should stay on this URL.',
    benefits: [
      {
        title: 'One spelling, one URL',
        text: 'Chengalpattu and the booking-list town Chengalpet share this page. A second slug is not published.',
      },
      {
        title: 'South of the Chennai hub',
        text: 'Airport and city trips that start in Chennai stay on /chennai-taxi. This page is for a Chengalpattu district start.',
      },
      {
        title: 'Coast or capital drops',
        text: 'Chennai, Mahabalipuram and Kancheepuram can be entered as the drop. The fare is calculated only after that trip is mapped.',
      },
      {
        title: 'Same booking card',
        text: 'Sedan, SUV and Crysta, plus date and time, come from the existing Easy Drop Taxi form.',
      },
    ],
    howTo: [
      'Keep Chengalpattu as pickup, or replace it with Chengalpet, Mahabalipuram or another mapped address in the district. Add the drop and your mobile number, then tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta and the date and time you want to leave Chengalpattu.',
      'Review the trip and confirm. Confirmation uses the existing Easy Drop Taxi booking system.',
    ],
    faqs: [
      {
        question: 'Is Chengalpet a different district page?',
        answer:
          'No. Chengalpet is the town spelling already stored in the homepage booking list. The district page is Chengalpattu at /chengalpattu-taxi. Both refer to this booking, not to a second URL.',
      },
      {
        question: 'Should a Chengalpattu trip use the Chennai Taxi hub?',
        answer:
          'Use /chennai-taxi when pickup is in Chennai. Use this page when the cab starts in Chengalpattu district, including a Mahabalipuram or GST-road boarding point.',
      },
      {
        question: 'How is the Chengalpattu fare calculated?',
        answer:
          'The form uses the published per-kilometre tariff on the mapped drive from the pickup and drop you enter. This page does not add a Chengalpattu package price.',
      },
      {
        question: 'Does Mahabalipuram have its own taxi URL?',
        answer:
          'No. Mahabalipuram is a town in Chengalpattu district. Type it as pickup or drop on this page. It is not a separate district.',
      },
    ],
    sampleDrops: ['Chennai', 'Mahabalipuram', 'Kancheepuram'],
    related: [
      { path: '/chennai-taxi', label: 'Chennai Taxi' },
      { path: '/kancheepuram-taxi', label: 'Kancheepuram Taxi' },
      { path: '/tariff', label: 'Tariff' },
      { path: '/', label: 'Home' },
    ],
  },
  {
    name: 'Dharmapuri',
    heroLead:
      'Start a one-way taxi in Dharmapuri district, between Salem and the Krishnagiri border, and name the drop in the booking card.',
    seoDescription:
      'Book a one-way taxi from Dharmapuri with Easy Drop Taxi. Dharmapuri is its own district page. Hosur stays on the existing Coimbatore–Hosur route in Krishnagiri district.',
    intro: [
      'Dharmapuri district lies in northwestern Tamil Nadu, between Salem and Krishnagiri. The headquarters is Dharmapuri town. Hogenakkal Falls, on the Cauvery, is in this district. This page is for pickups that start here.',
      'Hosur is not Dharmapuri. Hosur is in Krishnagiri district and already has /coimbatore-to-hosur-taxi and the reverse route. Those URLs stay as they are. A Dharmapuri booking does not create a Hosur duplicate.',
      'Salem already has /salem-taxi. Open the Salem hub when the trip starts in Salem. Stay on this page when the boarding point is Dharmapuri district.',
    ],
    placesNote:
      'Palacode, Harur and Pennagaram are towns in Dharmapuri district. Type the town if that is the real pickup. They do not receive separate district URLs.',
    serviceNote:
      'No catalogue kilometre for Dharmapuri is printed here. The booking card measures the trip you enter.',
    benefits: [
      {
        title: 'Separate from Salem',
        text: 'Salem Taxi remains the Salem hub. Dharmapuri travellers start on this district page.',
      },
      {
        title: 'Hosur stays a Krishnagiri route',
        text: 'Existing Hosur route pages are not relabelled as Dharmapuri, and this page does not add another Hosur slug.',
      },
      {
        title: 'Hogenakkal is a place, not a page',
        text: 'Enter Hogenakkal as pickup or drop when that is the stop. It remains inside Dharmapuri district.',
      },
      {
        title: 'Same booking card',
        text: 'The estimate, vehicle choice and confirmation follow the homepage Easy Drop Taxi flow.',
      },
    ],
    howTo: [
      'Keep Dharmapuri as pickup, or type Harur, Palacode or the exact address. Add the drop and your mobile number, then tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta and the leave date and time from Dharmapuri.',
      'Review the mapped fare and confirm through the existing booking system.',
    ],
    faqs: [
      {
        question: 'Is the Hosur taxi page the Dharmapuri district page?',
        answer:
          'No. Coimbatore to Hosur Taxi is a Krishnagiri-district route that is already published. This /dharmapuri-taxi page is only for Dharmapuri district.',
      },
      {
        question: 'How do I book a taxi from Dharmapuri town?',
        answer:
          'Leave pickup as Dharmapuri, set the drop, add your 10-digit mobile number, and tap Get Fare Estimate. Then choose the vehicle and travel time.',
      },
      {
        question: 'Can I book Dharmapuri to Salem or Dharmapuri to Bangalore?',
        answer:
          'Yes, by entering Salem or Bangalore as the drop on this page. That does not create a new route URL. Salem to Bangalore already has its own page from the Salem hub, which is a different origin.',
      },
      {
        question: 'Does this page quote a Dharmapuri package?',
        answer:
          'No. Fare comes from the live mapped distance and the published vehicle rate plus driver bata, the same way other Easy Drop Taxi estimates work.',
      },
    ],
    sampleDrops: ['Salem', 'Hosur', 'Bangalore'],
    related: [
      { path: '/salem-taxi', label: 'Salem Taxi' },
      { path: '/coimbatore-to-hosur-taxi', label: 'Coimbatore to Hosur Taxi' },
      { path: '/bangalore-taxi', label: 'Bangalore Taxi' },
      { path: '/tariff', label: 'Tariff' },
    ],
  },
  {
    name: 'Dindigul',
    heroLead:
      'Book the cab from Dindigul district when the journey starts between Madurai, the Palani hills and the Karur side of the plains.',
    seoDescription:
      'Book a one-way taxi from Dindigul with Easy Drop Taxi. Dindigul, Kodaikanal and Palani share this district page. No separate Dindigul route URL is added.',
    intro: [
      'Dindigul district sits south of Karur and north of Madurai, with the Palani hills on its western side. The headquarters is Dindigul. Kodaikanal and Palani are in this district. None of those hill towns has its own taxi URL on this site.',
      'The hospital travel guide and the Singanallur page mention Dindigul as a place travellers come from. Those mentions are not a Dindigul landing. This /dindigul-taxi page is that landing.',
      'Madurai Taxi and Trichy Taxi remain the hubs for trips that start in those cities. The Coimbatore–Karur corridor stays a Karur page, not a Dindigul page.',
    ],
    placesNote:
      'Oddanchatram, Batlagundu, Palani and Kodaikanal can be typed as pickup or drop. Typing them does not publish a new district or a new route slug.',
    serviceNote:
      'Easy Drop Taxi does not show a fixed Dindigul-to-Kodaikanal kilometre on this page. Hill traffic and the mapped drive are handled when you estimate.',
    benefits: [
      {
        title: 'Hill towns stay inside Dindigul',
        text: 'Kodaikanal and Palani are booked from this district page. They are not additional districts.',
      },
      {
        title: 'Existing hubs stay put',
        text: 'Madurai, Trichy and the Karur corridor keep their current URLs. This page does not copy them.',
      },
      {
        title: 'Plains or hill drop',
        text: 'Madurai, Trichy and Kodaikanal can be entered as the drop. The fare waits for the mapped trip.',
      },
      {
        title: 'Same booking card',
        text: 'Vehicle and confirmation use the existing Easy Drop Taxi form.',
      },
    ],
    howTo: [
      'Set pickup to Dindigul, Palani, Kodaikanal or the exact address. Add the drop and your mobile number, then tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta and the date and time you leave.',
      'Review the fare and confirm. The request goes through the existing booking system.',
    ],
    faqs: [
      {
        question: 'Is there already a Dindigul to Coimbatore taxi page?',
        answer:
          'No. Older copy mentions Dindigul as a travel origin, and the Singanallur page lists it as a possible drop. Neither of those is a Dindigul route URL. Book the trip from this district page.',
      },
      {
        question: 'Does Kodaikanal get a separate taxi URL?',
        answer:
          'No. Kodaikanal is in Dindigul district. Enter it as pickup or drop here. Do not look for a /kodaikanal-taxi page.',
      },
      {
        question: 'Is the Karur corridor the Dindigul page?',
        answer:
          'No. /karur-to-coimbatore and /coimbatore-to-karur are Karur district corridors. Use them only when Karur is an endpoint. A Dindigul start belongs on this page.',
      },
      {
        question: 'How is a Dindigul fare calculated?',
        answer:
          'After you enter pickup and drop, the form maps the drive and applies the published one-way tariff. This page does not invent a Dindigul package.',
      },
    ],
    sampleDrops: ['Madurai', 'Kodaikanal', 'Trichy'],
    related: [
      { path: '/madurai-taxi', label: 'Madurai Taxi' },
      { path: '/trichy-taxi', label: 'Trichy Taxi' },
      { path: '/karur-to-coimbatore', label: 'Karur to Coimbatore' },
      { path: '/tariff', label: 'Tariff' },
    ],
  },
  {
    name: 'Erode',
    heroLead:
      'Start a one-way taxi in Erode district, on the road between Salem and Coimbatore, and choose the drop yourself.',
    seoDescription:
      'Book a one-way taxi from Erode district with Easy Drop Taxi. Erode has this district page. Bhavani remains a town label in the booking list, not a second URL.',
    intro: [
      'Erode district lies on the western corridor between Salem and Coimbatore. The headquarters is Erode town, a textile and turmeric market on the Cauvery. Bhavani, Gobichettipalayam and Sathyamangalam are towns in the same district.',
      'The homepage booking list includes “Bhavani (Erode Dt)” as a town, which tells the passenger the district. It is not an Erode page and it is not a Bhavani page. This /erode-taxi URL is the district landing.',
      'Trips that start in Salem use /salem-taxi. Trips that start in Coimbatore use /coimbatore-taxi. Use this page when the cab starts in Erode district.',
    ],
    placesNote:
      'Bhavani, Gobichettipalayam, Perundurai and Sathyamangalam can replace the pickup text when that town is the boarding point. They stay on this district URL.',
    serviceNote:
      'No Erode-to-Coimbatore route slug is added. Enter Coimbatore, Salem or another drop in the booking card if that is the journey.',
    benefits: [
      {
        title: 'Between two existing hubs',
        text: 'Salem and Coimbatore keep their hubs. Erode is the district between them, with its own pickup page.',
      },
      {
        title: 'Bhavani is not a new district',
        text: 'The booking-list label names Erode district so passengers can tell towns apart. It does not create /bhavani-taxi.',
      },
      {
        title: 'Market or highway pickup',
        text: 'Type the stand, mill or village if Erode town is not the exact boarding point.',
      },
      {
        title: 'Same booking card',
        text: 'The fare estimate and confirmation use the existing Easy Drop Taxi form.',
      },
    ],
    howTo: [
      'Keep Erode as pickup, or change it to Bhavani, Gobichettipalayam or another address in the district. Add the drop and your mobile number, then tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta and the leave date and time.',
      'Review the mapped trip and confirm through the existing booking system.',
    ],
    faqs: [
      {
        question: 'Is Bhavani (Erode Dt) its own taxi page?',
        answer:
          'No. That string is a town in the homepage booking list. The district page is /erode-taxi. Set pickup to Bhavani if that is where the cab should arrive.',
      },
      {
        question: 'Should I open Coimbatore Taxi for an Erode pickup?',
        answer:
          'Open /coimbatore-taxi when the trip starts in Coimbatore. Open this page when it starts in Erode district, even if the drop is Coimbatore.',
      },
      {
        question: 'Can I book Erode to Salem as one way?',
        answer:
          'Yes. Enter Salem as the drop and leave One Way selected unless you also need the return. This does not publish a new Erode-to-Salem URL.',
      },
      {
        question: 'Does this page show an Erode fare chart?',
        answer:
          'No district chart is added. The booking card uses the published tariff on the distance it maps for your pickup and drop.',
      },
    ],
    sampleDrops: ['Coimbatore', 'Salem', 'Tiruppur'],
    related: [
      { path: '/salem-taxi', label: 'Salem Taxi' },
      { path: '/coimbatore-taxi', label: 'Coimbatore Taxi' },
      { path: '/coimbatore-to-tiruppur', label: 'Coimbatore to Tiruppur' },
      { path: '/tariff', label: 'Tariff' },
    ],
  },
  {
    name: 'Kallakurichi',
    heroLead:
      'Book a one-way taxi from Kallakurichi district, west of Viluppuram, without using the existing Villupuram route as a substitute.',
    seoDescription:
      'Book a one-way taxi from Kallakurichi with Easy Drop Taxi. Kallakurichi is its own district. The published Villupuram spelling stays on the Coimbatore–Villupuram route.',
    intro: [
      'Kallakurichi is a district on the northwestern side of Viluppuram, below the Kalvarayan Hills. The headquarters is Kallakurichi town. This page uses that district name and no alternate slug.',
      'Viluppuram already appears on the site as Villupuram, on /coimbatore-to-villupuram-taxi and the reverse. That pair starts in Coimbatore and ends in Villupuram. It is not a Kallakurichi page, and the spelling is not changed.',
      'Use this page when the pickup is in Kallakurichi district. Use the Villupuram route only when that existing Coimbatore pair is the trip you want.',
    ],
    placesNote:
      'Ulundurpet, Sankarapuram and the Kalvarayan hill villages are in Kallakurichi district. Enter the place name as pickup or drop. They do not become new districts.',
    serviceNote:
      'Salem Taxi remains the hub when the trip starts in Salem. Kallakurichi does not borrow that URL.',
    benefits: [
      {
        title: 'Not the Villupuram route',
        text: 'The Coimbatore–Villupuram pages keep their published spelling and their Coimbatore origin.',
      },
      {
        title: 'Hill or highway pickup',
        text: 'Replace Kallakurichi with the town or hill village where the cab should wait.',
      },
      {
        title: 'One district URL',
        text: 'Only /kallakurichi-taxi is added. No second spelling is published.',
      },
      {
        title: 'Same booking card',
        text: 'Estimate and confirmation stay on the existing Easy Drop Taxi form.',
      },
    ],
    howTo: [
      'Keep Kallakurichi as pickup, or type Ulundurpet or the exact address. Add the drop and your mobile number, then tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta and the date and time you leave Kallakurichi.',
      'Review the fare and confirm. The booking uses the existing system.',
    ],
    faqs: [
      {
        question: 'Is Kallakurichi included in the Villupuram taxi route?',
        answer:
          'The published route is Coimbatore to Villupuram, using the site spelling Villupuram. Kallakurichi is a separate district and is booked from /kallakurichi-taxi.',
      },
      {
        question: 'How do I book a taxi from Kallakurichi town?',
        answer:
          'Leave pickup as Kallakurichi, enter the drop, add your mobile number, and tap Get Fare Estimate. Then select the vehicle and travel time.',
      },
      {
        question: 'Can the drop be Viluppuram or Salem?',
        answer:
          'Yes. Type Villupuram or Salem in the drop field on this page. That fills the booking card. It does not create a Kallakurichi route URL.',
      },
      {
        question: 'Does this page quote a Kallakurichi kilometre?',
        answer:
          'No. The form maps the pickup and drop you submit and applies the same one-way tariff used on the rest of the site.',
      },
    ],
    sampleDrops: ['Villupuram', 'Salem', 'Chennai'],
    related: [
      { path: '/coimbatore-to-villupuram-taxi', label: 'Coimbatore to Villupuram Taxi' },
      { path: '/salem-taxi', label: 'Salem Taxi' },
      { path: '/chennai-taxi', label: 'Chennai Taxi' },
      { path: '/tariff', label: 'Tariff' },
    ],
  },
  {
    name: 'Kancheepuram',
    heroLead:
      'Book a one-way taxi from Kancheepuram district, the temple city west of Chennai, on this spelling only.',
    seoDescription:
      'Book a one-way taxi from Kancheepuram with Easy Drop Taxi. Kanchipuram is the same district. Chengalpattu has its own page and is not booked from this URL.',
    intro: [
      'Kancheepuram district is the temple town and silk-weaving belt west of Chennai. The headquarters is Kancheepuram, also spelled Kanchipuram. This site uses the audit spelling Kancheepuram for the only district URL.',
      'Chengalpattu was formed from the eastern part of the old Kancheepuram district and has its own page at /chengalpattu-taxi. A pickup in Chengalpattu, Mahabalipuram or along that coast belongs on the Chengalpattu page.',
      'Chennai Taxi remains the hub for a Chennai start. Vellore routes that already exist, such as Coimbatore to Vellore, stay on those URLs and are not copied here.',
    ],
    placesNote:
      'Sriperumbudur and Uthiramerur are towns in Kancheepuram district. Type them when they are the boarding point. They are not separate district pages.',
    serviceNote:
      'Searching Kanchipuram or Kancheepuram should land here. A second slug for the other spelling is not created.',
    benefits: [
      {
        title: 'One spelling on the URL',
        text: 'Kancheepuram is the published slug. Kanchipuram names the same district and the same page.',
      },
      {
        title: 'Chengalpattu stays separate',
        text: 'Coastal and GST pickups in Chengalpattu district use /chengalpattu-taxi.',
      },
      {
        title: 'Temple-town pickup',
        text: 'Edit the pickup if the cab should wait at a specific street or at Sriperumbudur rather than the town centre.',
      },
      {
        title: 'Same booking card',
        text: 'The existing form calculates the fare after you enter the drop.',
      },
    ],
    howTo: [
      'Keep Kancheepuram as pickup, or replace it with Kanchipuram, Sriperumbudur or the exact address. Add the drop and your mobile number, then tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta and the leave date and time.',
      'Review the mapped fare and confirm through the existing booking system.',
    ],
    faqs: [
      {
        question: 'Is Kanchipuram a second page?',
        answer:
          'No. Kanchipuram and Kancheepuram are spellings of the same district. The only URL added is /kancheepuram-taxi.',
      },
      {
        question: 'Is Chengalpattu booked on the Kancheepuram page?',
        answer:
          'No. Chengalpattu has /chengalpattu-taxi. Use that page when the pickup is in Chengalpattu district.',
      },
      {
        question: 'How do I book a taxi from Kancheepuram to Chennai?',
        answer:
          'Set the drop to Chennai on this page, add your mobile number, and tap Get Fare Estimate. That does not create a new Kancheepuram-to-Chennai route URL. The Chennai hub remains /chennai-taxi for trips that start in Chennai.',
      },
      {
        question: 'Does this page list a Kancheepuram package fare?',
        answer:
          'No. The booking card uses the published tariff on the drive it maps. No district package is added.',
      },
    ],
    sampleDrops: ['Chennai', 'Chengalpattu', 'Vellore'],
    related: [
      { path: '/chennai-taxi', label: 'Chennai Taxi' },
      { path: '/chengalpattu-taxi', label: 'Chengalpattu Taxi' },
      { path: '/coimbatore-to-vellore-taxi', label: 'Coimbatore to Vellore Taxi' },
      { path: '/tariff', label: 'Tariff' },
    ],
  },
  {
    name: 'Mayiladuthurai',
    heroLead:
      'Start a one-way taxi in Mayiladuthurai district, in the Cauvery delta, without reusing the Thanjavur or Nagapatinam routes as this page.',
    seoDescription:
      'Book a one-way taxi from Mayiladuthurai with Easy Drop Taxi. Thanjavur and Nagapatinam keep their existing Coimbatore routes. This URL is only Mayiladuthurai district.',
    intro: [
      'Mayiladuthurai is the Cauvery-delta district between Thanjavur and Nagapattinam. The headquarters is Mayiladuthurai, known for the Mayuranathaswami temple. Sirkazhi and Poompuhar are in this district.',
      'The site already publishes Coimbatore to Thanjavur and Coimbatore to Nagapatinam, using the spelling Nagapatinam. Those routes start in Coimbatore. They are not Mayiladuthurai pages, and their spellings stay as published.',
      'Book from this page when the cab starts in Mayiladuthurai district. Open the Thanjavur or Nagapatinam route only when that existing Coimbatore pair is the journey.',
    ],
    placesNote:
      'Sirkazhi, Poompuhar and Tharangambadi are towns in Mayiladuthurai district. Type the town as pickup when that is the boarding point. None of them gets a separate district URL.',
    serviceNote:
      'No Mayiladuthurai-to-Chennai route slug is created. Enter Chennai, Thanjavur or another drop in the booking card.',
    benefits: [
      {
        title: 'Delta district on its own URL',
        text: 'Mayiladuthurai is not folded into the Thanjavur or Nagapatinam route pages.',
      },
      {
        title: 'Published spellings stay',
        text: 'Nagapatinam remains the spelling on the existing Coimbatore route. This page does not add a second Nagapattinam slug.',
      },
      {
        title: 'Temple-town or coast pickup',
        text: 'Change pickup to Sirkazhi, Poompuhar or a street address when Mayiladuthurai town is not the boarding point.',
      },
      {
        title: 'Same booking card',
        text: 'Fare and confirmation use the existing Easy Drop Taxi form.',
      },
    ],
    howTo: [
      'Keep Mayiladuthurai as pickup, or type Sirkazhi or Poompuhar. Add the drop and your mobile number, then tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta and the date and time you leave.',
      'Review the mapped fare and confirm through the existing booking system.',
    ],
    faqs: [
      {
        question: 'Is Mayiladuthurai part of the Nagapatinam taxi route?',
        answer:
          'The published page is Coimbatore to Nagapatinam. Mayiladuthurai is a separate district and is booked from /mayiladuthurai-taxi.',
      },
      {
        question: 'Is the Thanjavur route enough for a Mayiladuthurai pickup?',
        answer:
          'No. Coimbatore to Thanjavur Taxi starts in Coimbatore and ends in Thanjavur. A pickup in Mayiladuthurai starts on this page.',
      },
      {
        question: 'Can I drop at Thanjavur or Nagapattinam from here?',
        answer:
          'Yes. Type Thanjavur or Nagapattinam as the drop. The site’s existing route spelling for the Coimbatore pair is Nagapatinam. Either drop text is a booking field, not a new URL.',
      },
      {
        question: 'Does this page show a delta package fare?',
        answer:
          'No. The form maps your pickup and drop and uses the same one-way tariff as the rest of Easy Drop Taxi.',
      },
    ],
    sampleDrops: ['Thanjavur', 'Nagapattinam', 'Chennai'],
    related: [
      { path: '/coimbatore-to-thanjavur-taxi', label: 'Coimbatore to Thanjavur Taxi' },
      { path: '/coimbatore-to-nagapatinam-taxi', label: 'Coimbatore to Nagapatinam Taxi' },
      { path: '/trichy-taxi', label: 'Trichy Taxi' },
      { path: '/tariff', label: 'Tariff' },
    ],
  },
  {
    name: 'Namakkal',
    heroLead:
      'Book a one-way taxi from Namakkal district, between Salem and Karur, including pickups toward Kolli Hills.',
    seoDescription:
      'Book a one-way taxi from Namakkal with Easy Drop Taxi. Salem keeps /salem-taxi and Karur keeps its Coimbatore corridor. This page is only Namakkal district.',
    intro: [
      'Namakkal district sits between Salem and Karur. The headquarters is Namakkal, a transport town. Kolli Hills are in this district. This page is the Namakkal pickup landing.',
      'Salem already has a city hub. Karur already has /karur-to-coimbatore and /coimbatore-to-karur, without a -taxi suffix. Those URLs stay exactly as published. A Namakkal trip does not add a parallel Karur or Salem slug.',
      'Use /salem-taxi when the cab starts in Salem, and the Karur corridor when Karur is the endpoint of that Coimbatore trip. Use this page when the boarding point is in Namakkal district.',
    ],
    placesNote:
      'Tiruchengode, Rasipuram, Paramathi Velur and Kolli Hills can be typed as the pickup. They remain Namakkal district places, not new pages.',
    serviceNote:
      'No fixed Namakkal-to-Coimbatore distance is printed. The booking card maps the addresses you enter.',
    benefits: [
      {
        title: 'Between Salem and Karur',
        text: 'Those existing pages stay in place. Namakkal travellers start here.',
      },
      {
        title: 'Kolli Hills included',
        text: 'A hill pickup is still Namakkal district. Type Kolli Hills or the village name. No /kolli-hills URL is added.',
      },
      {
        title: 'Transport-town pickup',
        text: 'Replace Namakkal with the lorry stand, Tiruchengode or Rasipuram if that is where the passenger will wait.',
      },
      {
        title: 'Same booking card',
        text: 'Vehicle choice and confirmation use the existing Easy Drop Taxi form.',
      },
    ],
    howTo: [
      'Keep Namakkal as pickup, or type Tiruchengode, Rasipuram or Kolli Hills. Add the drop and your mobile number, then tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta and the leave date and time.',
      'Review the mapped fare and confirm through the existing booking system.',
    ],
    faqs: [
      {
        question: 'Is Namakkal covered by the Salem Taxi hub?',
        answer:
          'Salem Taxi is the Salem start. Namakkal district starts on /namakkal-taxi, even when the drop is Salem.',
      },
      {
        question: 'Is the Karur corridor a Namakkal page?',
        answer:
          'No. /karur-to-coimbatore and /coimbatore-to-karur are the existing Karur corridors. Use them when Karur is an endpoint of that trip.',
      },
      {
        question: 'Can I book Namakkal to Coimbatore?',
        answer:
          'Yes. Enter Coimbatore as the drop on this page. That does not publish a new Namakkal-to-Coimbatore URL.',
      },
      {
        question: 'Does Kolli Hills have a separate fare on this page?',
        answer:
          'No separate hill package is shown. Enter the Kolli Hills boarding point and the drop, then let the form map the drive.',
      },
    ],
    sampleDrops: ['Salem', 'Karur', 'Coimbatore'],
    related: [
      { path: '/salem-taxi', label: 'Salem Taxi' },
      { path: '/karur-to-coimbatore', label: 'Karur to Coimbatore' },
      { path: '/coimbatore-taxi', label: 'Coimbatore Taxi' },
      { path: '/tariff', label: 'Tariff' },
    ],
  },
  {
    name: 'Perambalur',
    heroLead:
      'Book a one-way taxi from Perambalur district, the small central district between Tiruchirappalli and Ariyalur.',
    seoDescription:
      'Book a one-way taxi from Perambalur with Easy Drop Taxi. Perambalur is not the Trichy hub and not the Ariyalur page. Pickup starts in Perambalur district.',
    intro: [
      'Perambalur is a small inland district with its headquarters at Perambalur town, between Tiruchirappalli and Ariyalur. This page is only that district.',
      'Trichy already has /trichy-taxi. Ariyalur has its own district page at /ariyalur-taxi. A pickup in Perambalur should not be moved onto either of those URLs unless the passenger is actually boarding there.',
      'There is no existing Perambalur route in the sitemap. Drops such as Trichy, Ariyalur and Chennai are typed into the booking card on this page.',
    ],
    placesNote:
      'Veppanthattai and Alathur are towns in Perambalur district. Use the town name when it is the boarding point. They do not get their own URLs.',
    serviceNote:
      'The booking card is prefilled with Perambalur. Edit it before estimating if the cab should wait somewhere else.',
    benefits: [
      {
        title: 'Separate from Trichy',
        text: 'Tiruchirappalli bookings stay on /trichy-taxi. Perambalur starts here.',
      },
      {
        title: 'Separate from Ariyalur',
        text: 'The neighbouring district has /ariyalur-taxi. The two pages are not interchangeable.',
      },
      {
        title: 'Short-district pickup',
        text: 'Type the village or highway point if Perambalur town is only the district name and not the exact stop.',
      },
      {
        title: 'Same booking card',
        text: 'The existing form handles the estimate and confirmation.',
      },
    ],
    howTo: [
      'Keep Perambalur as pickup, enter the drop, add your mobile number, and tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta and the date and time you leave Perambalur.',
      'Review the mapped fare and confirm through the existing booking system.',
    ],
    faqs: [
      {
        question: 'Is Perambalur included in Trichy Taxi?',
        answer:
          'No. /trichy-taxi is Tiruchirappalli. Book a Perambalur start from /perambalur-taxi.',
      },
      {
        question: 'Is Perambalur the same page as Ariyalur?',
        answer:
          'No. They are neighbouring districts. Ariyalur is /ariyalur-taxi. Perambalur is this page.',
      },
      {
        question: 'Can I book Perambalur to Trichy as one way?',
        answer:
          'Yes. Set the drop to Trichy and leave One Way selected unless you need the cab to return. No separate Perambalur-to-Trichy URL is published.',
      },
      {
        question: 'Does this page show a Perambalur package price?',
        answer:
          'No. The fare is the published tariff applied to the drive the form maps.',
      },
    ],
    sampleDrops: ['Trichy', 'Ariyalur', 'Chennai'],
    related: [
      { path: '/trichy-taxi', label: 'Trichy Taxi' },
      { path: '/ariyalur-taxi', label: 'Ariyalur Taxi' },
      { path: '/tariff', label: 'Tariff' },
      { path: '/', label: 'Home' },
    ],
  },
  {
    name: 'Pudukkottai',
    heroLead:
      'Start a one-way taxi in Pudukkottai district, between Tiruchirappalli and the Ramanathapuram coast.',
    seoDescription:
      'Book a one-way taxi from Pudukkottai with Easy Drop Taxi. Alangudi (Pudukkottai) in the booking list is a town label. This URL is the district page.',
    intro: [
      'Pudukkottai district lies south-east of Tiruchirappalli, inland from the Ramanathapuram coast. The headquarters is Pudukkottai town. Alangudi is a town in the district.',
      'The homepage booking list says “Alangudi (Pudukkottai)” so it is not confused with Alangudi in Thiruvarur district. That label is not a page. This /pudukkottai-taxi URL is the district landing. The existing Thiruvarur route spelling stays /coimbatore-to-thiruvarur-taxi.',
      'Ramanathapuram already has /coimbatore-to-ramanathapuram-taxi, a Coimbatore origin. Trichy has /trichy-taxi. Use those when they match the trip. Use this page for a Pudukkottai district start.',
    ],
    placesNote:
      'Aranthangi, Illuppur and Alangudi can be typed as pickup. Aranthangi is also a booking-list town. None of them becomes a new district URL.',
    serviceNote:
      'No Pudukkottai-to-Rameshwaram slug is added. Rameshwaram routes that already exist stay on their Coimbatore and Chennai URLs.',
    benefits: [
      {
        title: 'Town label versus district page',
        text: 'Alangudi (Pudukkottai) identifies a town. The district booking page is this URL.',
      },
      {
        title: 'Not the Thiruvarur Alangudi',
        text: 'The other Alangudi is marked Thiruvarur in the booking list and follows the existing Thiruvarur route when that route is the trip.',
      },
      {
        title: 'Inland pickup',
        text: 'Edit the pickup for a village or bus stand if Pudukkottai town is not the waiting point.',
      },
      {
        title: 'Same booking card',
        text: 'The existing Easy Drop Taxi form calculates the fare after the drop is entered.',
      },
    ],
    howTo: [
      'Keep Pudukkottai as pickup, or type Alangudi or Aranthangi. Add the drop and your mobile number, then tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta and the leave date and time.',
      'Review the mapped fare and confirm through the existing booking system.',
    ],
    faqs: [
      {
        question: 'Does Alangudi (Pudukkottai) have its own taxi page?',
        answer:
          'No. It is a town in the booking list. Book it from /pudukkottai-taxi by setting that pickup.',
      },
      {
        question: 'Is the Ramanathapuram route the Pudukkottai page?',
        answer:
          'No. Coimbatore to Ramanathapuram Taxi starts in Coimbatore. A Pudukkottai pickup starts here.',
      },
      {
        question: 'Can I book Pudukkottai to Trichy or Rameshwaram?',
        answer:
          'Yes, by entering that drop on this page. Existing Rameshwaram routes from Coimbatore and Chennai are left as they are.',
      },
      {
        question: 'Is a Pudukkottai fare printed on this page?',
        answer:
          'No package fare is printed. The booking card maps the trip and applies the published one-way tariff.',
      },
    ],
    sampleDrops: ['Trichy', 'Rameshwaram', 'Madurai'],
    related: [
      { path: '/trichy-taxi', label: 'Trichy Taxi' },
      { path: '/coimbatore-to-ramanathapuram-taxi', label: 'Coimbatore to Ramanathapuram Taxi' },
      { path: '/coimbatore-to-thiruvarur-taxi', label: 'Coimbatore to Thiruvarur Taxi' },
      { path: '/tariff', label: 'Tariff' },
    ],
  },
  {
    name: 'Ranipet',
    heroLead:
      'Book a one-way taxi from Ranipet district, between Chennai and Vellore, without using the Ambur route as this page.',
    seoDescription:
      'Book a one-way taxi from Ranipet with Easy Drop Taxi. Ambur is Tirupathur district and already has a Coimbatore route. This page is only Ranipet.',
    intro: [
      'Ranipet district lies between Chennai and Vellore. The headquarters is Ranipet. Arcot and Walajapet are in this district. Industrial areas around Ranipet are pickups you type as an address, not separate pages.',
      'Ambur is not in Ranipet district. Ambur is in Tirupathur district and already has /coimbatore-to-ambur-taxi and the reverse. Those URLs stay. This page does not absorb Ambur and does not add a Tirupathur slug.',
      'Vellore already has Coimbatore and Madurai route pairs. Chennai has /chennai-taxi. Use those when the trip starts there. Use /ranipet-taxi when the cab starts in Ranipet district.',
    ],
    placesNote:
      'Arcot, Walajapet, Sholingur and Arakkonam-side addresses that fall in Ranipet district should be typed as the pickup. Arakkonam is a booking-list town. Confirm the address rather than assuming a new URL.',
    serviceNote:
      'No Ranipet-to-Chennai route page is created. Enter Chennai or Vellore as the drop on this booking card.',
    benefits: [
      {
        title: 'Ambur stays Tirupathur',
        text: 'The existing Ambur routes are not relabelled as Ranipet, and no Tirupathur page is added in this set.',
      },
      {
        title: 'Between Chennai and Vellore',
        text: 'Those published hubs and routes remain the pages for a Chennai or Vellore start.',
      },
      {
        title: 'Arcot included',
        text: 'Arcot is a town in Ranipet district. Type it as pickup. It does not get /arcot-taxi.',
      },
      {
        title: 'Same booking card',
        text: 'The existing form estimates the fare from the pickup and drop you enter.',
      },
    ],
    howTo: [
      'Keep Ranipet as pickup, or type Arcot, Walajapet or the exact gate address. Add the drop and your mobile number, then tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta and the leave date and time.',
      'Review the mapped fare and confirm through the existing booking system.',
    ],
    faqs: [
      {
        question: 'Is the Ambur taxi route the Ranipet district page?',
        answer:
          'No. Coimbatore to Ambur Taxi is the Tirupathur-district town already on the site. Ranipet is /ranipet-taxi.',
      },
      {
        question: 'Is Ranipet the same as Vellore?',
        answer:
          'No. Vellore has its own published routes, including Coimbatore to Vellore and Madurai to Vellore. A Ranipet pickup starts on this page.',
      },
      {
        question: 'Can I book Ranipet to Chennai as one way?',
        answer:
          'Yes. Set the drop to Chennai on this page. That does not create a new route slug. Trips that start in Chennai still use /chennai-taxi.',
      },
      {
        question: 'Does this page quote a Ranipet fare?',
        answer:
          'No district price is printed. The booking card applies the published tariff to the mapped drive.',
      },
    ],
    sampleDrops: ['Chennai', 'Vellore', 'Arcot'],
    related: [
      { path: '/chennai-taxi', label: 'Chennai Taxi' },
      { path: '/coimbatore-to-vellore-taxi', label: 'Coimbatore to Vellore Taxi' },
      { path: '/madurai-to-vellore-taxi', label: 'Madurai to Vellore Taxi' },
      { path: '/coimbatore-to-ambur-taxi', label: 'Coimbatore to Ambur Taxi' },
      { path: '/tariff', label: 'Tariff' },
    ],
  },
  {
    name: 'Sivaganga',
    heroLead:
      'Book a one-way taxi from Sivaganga district, between Madurai and Ramanathapuram, including Chettinad towns.',
    seoDescription:
      'Book a one-way taxi from Sivaganga with Easy Drop Taxi. Karaikudi and the Chettinad towns stay on this district page. No Sivagangai spelling URL is added.',
    intro: [
      'Sivaganga district lies between Madurai and Ramanathapuram. The headquarters is Sivaganga. Karaikudi, Devakottai and the Chettinad region are in this district. The audit name is Sivaganga, so the URL uses that spelling and not Sivagangai.',
      'Madurai already has /madurai-taxi. Ramanathapuram already has a Coimbatore route pair, and Rameshwaram has its own existing routes. Those pages stay. This page is only a Sivaganga district start.',
      'There is no Karaikudi taxi URL. Enter Karaikudi as pickup or drop on this page when that is the town.',
    ],
    placesNote:
      'Karaikudi, Devakottai, Manamadurai and Tiruppattur in Sivaganga district are places you can type. Tiruppattur here is not Tirupathur district, and it is not given a new page.',
    serviceNote:
      'The booking card prefills Sivaganga. Change it to Karaikudi or another town before estimating if that is the boarding point.',
    benefits: [
      {
        title: 'One spelling',
        text: 'Sivaganga is the published district URL. A Sivagangai slug is not added.',
      },
      {
        title: 'Chettinad towns included',
        text: 'Karaikudi and Devakottai are booked from this page. They are not extra districts.',
      },
      {
        title: 'Neighbouring pages stay',
        text: 'Madurai hub, Ramanathapuram routes and Rameshwaram routes keep their current URLs.',
      },
      {
        title: 'Same booking card',
        text: 'The existing Easy Drop Taxi form handles the estimate and confirmation.',
      },
    ],
    howTo: [
      'Keep Sivaganga as pickup, or type Karaikudi or Devakottai. Add the drop and your mobile number, then tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta and the leave date and time.',
      'Review the mapped fare and confirm through the existing booking system.',
    ],
    faqs: [
      {
        question: 'Is Sivagangai a different URL?',
        answer:
          'No. The district page uses the audit spelling Sivaganga at /sivaganga-taxi. A second spelling is not published.',
      },
      {
        question: 'Does Karaikudi have its own taxi page?',
        answer:
          'No. Karaikudi is in Sivaganga district. Set it as pickup or drop on this page.',
      },
      {
        question: 'Is Ramanathapuram Taxi the Sivaganga page?',
        answer:
          'No. Coimbatore to Ramanathapuram Taxi is an existing route with a Coimbatore start. Sivaganga starts here.',
      },
      {
        question: 'How is the Sivaganga fare calculated?',
        answer:
          'The form maps the pickup and drop you enter and applies the published one-way tariff. No Sivaganga package is shown.',
      },
    ],
    sampleDrops: ['Madurai', 'Karaikudi', 'Rameshwaram'],
    related: [
      { path: '/madurai-taxi', label: 'Madurai Taxi' },
      { path: '/coimbatore-to-ramanathapuram-taxi', label: 'Coimbatore to Ramanathapuram Taxi' },
      { path: '/tariff', label: 'Tariff' },
      { path: '/', label: 'Home' },
    ],
  },
  {
    name: 'Theni',
    heroLead:
      'Book a one-way taxi from Theni district, west of Madurai toward the Kerala border, on a page of its own.',
    seoDescription:
      'Book a one-way taxi from Theni with Easy Drop Taxi. Mentions of Theni on the Singanallur page are not a route. This URL is the Theni district page.',
    intro: [
      'Theni district is west of Madurai, in the valley that leads toward the Kerala hills. The headquarters is Theni. Bodi, Cumbum and the Suruli area are in this district. This page is the Theni landing.',
      'The Singanallur taxi page names Theni as a possible drop from Coimbatore, and its copy says to ask for a fare. That is not a Theni URL. No /coimbatore-to-theni-taxi page exists, and this change does not create one.',
      'Madurai Taxi remains the hub for a Madurai start. Use /theni-taxi when the cab starts in Theni district.',
    ],
    placesNote:
      'Bodi, Cumbum, Chinnamanur, Andipatti and Suruli can be typed as pickup or drop. Cumbum and Bodi already appear as town names in the booking list. They stay towns, not new districts.',
    serviceNote:
      'A Kerala drop such as Kumily or Theni to Madurai is entered in the booking card. No new interstate route URL is added.',
    benefits: [
      {
        title: 'Its own district URL',
        text: 'Theni is no longer only a name in Singanallur copy. The landing is /theni-taxi.',
      },
      {
        title: 'No invented Coimbatore pair',
        text: 'The Coimbatore–Theni trip is booked by setting those fields. A new route slug is not published.',
      },
      {
        title: 'Valley towns included',
        text: 'Bodi and Cumbum are Theni district pickups. Type the town. Do not look for a separate page.',
      },
      {
        title: 'Same booking card',
        text: 'The existing form maps the fare after pickup and drop are set.',
      },
    ],
    howTo: [
      'Keep Theni as pickup, or type Bodi, Cumbum or the exact address. Add the drop and your mobile number, then tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta and the leave date and time.',
      'Review the mapped fare and confirm through the existing booking system.',
    ],
    faqs: [
      {
        question: 'Is the Singanallur page a Theni taxi page?',
        answer:
          'No. Singanallur Taxi is a Coimbatore locality page that mentions Theni as a drop. The Theni district page is /theni-taxi.',
      },
      {
        question: 'Does Bodi or Cumbum have a separate URL?',
        answer:
          'No. Both are in Theni district. Enter the town as pickup or drop on this page.',
      },
      {
        question: 'Can I book Theni to Madurai as one way?',
        answer:
          'Yes. Set the drop to Madurai. Trips that start in Madurai still use /madurai-taxi. No Theni-to-Madurai slug is added.',
      },
      {
        question: 'Is a Theni package fare shown here?',
        answer:
          'No. The booking card uses the published tariff on the drive it maps for the addresses you enter.',
      },
    ],
    sampleDrops: ['Madurai', 'Bodi', 'Cumbum'],
    related: [
      { path: '/madurai-taxi', label: 'Madurai Taxi' },
      { path: '/singanallur-taxi', label: 'Singanallur Taxi' },
      { path: '/coimbatore-taxi', label: 'Coimbatore Taxi' },
      { path: '/tariff', label: 'Tariff' },
    ],
  },
  {
    name: 'Tiruvallur',
    heroLead:
      'Book a one-way taxi from Tiruvallur district, on Chennai’s northern and western side, using this spelling only.',
    seoDescription:
      'Book a one-way taxi from Tiruvallur with Easy Drop Taxi. The district URL is /tiruvallur-taxi. A Thiruvallur spelling page is not created. Chennai keeps its own hub.',
    intro: [
      'Tiruvallur district wraps the northern and western side of Chennai and continues toward the Andhra Pradesh border. The headquarters is Tiruvallur town. The audit spelling is Tiruvallur, so this URL does not use Thiruvallur.',
      'Chennai already has /chennai-taxi, including travellers who start at Chennai or Chennai Airport. Use the Chennai hub for a Chennai pickup. Use this page when the cab starts in Tiruvallur district.',
      'No Tiruvallur-to-Chennai route slug is added. Enter Chennai, or a town inside the district, in the booking card.',
    ],
    placesNote:
      'Ponneri, Gummidipoondi, Tiruvallur town and Avadi addresses in this district can be typed as pickup. They are not separate district pages.',
    serviceNote:
      'If a search uses the Thiruvallur spelling, this is still the page. A second slug is not published.',
    benefits: [
      {
        title: 'Audit spelling on the URL',
        text: 'Tiruvallur is the only new slug. Thiruvallur is not given another page.',
      },
      {
        title: 'Chennai hub unchanged',
        text: 'City and airport starts stay on /chennai-taxi.',
      },
      {
        title: 'Border or suburb pickup',
        text: 'Type Ponneri, Gummidipoondi or the exact address when Tiruvallur town is not the waiting point.',
      },
      {
        title: 'Same booking card',
        text: 'Estimate and confirmation use the existing Easy Drop Taxi form.',
      },
    ],
    howTo: [
      'Keep Tiruvallur as pickup, or type the town or address where the cab should wait. Add the drop and your mobile number, then tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta and the leave date and time.',
      'Review the mapped fare and confirm through the existing booking system.',
    ],
    faqs: [
      {
        question: 'Is Thiruvallur a different district page?',
        answer:
          'No. This site publishes /tiruvallur-taxi for the district named Tiruvallur in the audit. A Thiruvallur URL is not added.',
      },
      {
        question: 'Should Tiruvallur trips use Chennai Taxi?',
        answer:
          'Use /chennai-taxi when pickup is in Chennai. Use this page when pickup is in Tiruvallur district.',
      },
      {
        question: 'Can I book Tiruvallur to Chennai as one way?',
        answer:
          'Yes. Set the drop to Chennai on this page and leave One Way selected unless you need a round trip. That booking does not create a new route URL.',
      },
      {
        question: 'Does this page show a Tiruvallur fare chart?',
        answer:
          'No. The booking card maps the pickup and drop and applies the published one-way tariff.',
      },
    ],
    sampleDrops: ['Chennai', 'Ponneri', 'Chennai Airport'],
    related: [
      { path: '/chennai-taxi', label: 'Chennai Taxi' },
      { path: '/tariff', label: 'Tariff' },
      { path: '/taxi-routes', label: 'All Taxi Routes' },
      { path: '/', label: 'Home' },
    ],
  },
  {
    name: 'Virudhunagar',
    heroLead:
      'Start a one-way taxi in Virudhunagar district, south of Madurai on the way toward Tirunelveli.',
    seoDescription:
      'Book a one-way taxi from Virudhunagar with Easy Drop Taxi. Sivakasi and Rajapalayam stay in this district. Existing Tirunelveli routes keep both published spellings.',
    intro: [
      'Virudhunagar district is south of Madurai and north of Tirunelveli. The headquarters is Virudhunagar town, a trading centre. Sivakasi, Rajapalayam and Aruppukkottai are towns in this district.',
      'Tirunelveli is already published twice, as /coimbatore-to-thirunelveli-taxi and /chennai-to-tirunelveli-taxi. Those spellings stay. They are not Virudhunagar pages, and this change does not add a third Tirunelveli slug.',
      'Madurai Taxi remains the hub for a Madurai start. Aruppukkottai is already a town in the booking list. It does not become its own district. Book a Virudhunagar district start from this page.',
    ],
    placesNote:
      'Sivakasi, Rajapalayam, Srivilliputhur and Aruppukkottai can be typed as pickup or drop. Srivilliputhur and the Andal temple town stay inside this district page.',
    serviceNote:
      'No Virudhunagar-to-Madurai route URL is created. Enter Madurai, Sivakasi or Tirunelveli in the booking card.',
    benefits: [
      {
        title: 'Trading towns included',
        text: 'Sivakasi and Rajapalayam are Virudhunagar district pickups, not extra districts.',
      },
      {
        title: 'Tirunelveli spellings untouched',
        text: 'The existing thirunelveli and tirunelveli route slugs stay as published.',
      },
      {
        title: 'Madurai hub stays the Madurai start',
        text: 'Open /madurai-taxi when the cab starts in Madurai, even if the drop is Virudhunagar.',
      },
      {
        title: 'Same booking card',
        text: 'The existing Easy Drop Taxi form estimates and confirms the trip.',
      },
    ],
    howTo: [
      'Keep Virudhunagar as pickup, or type Sivakasi, Rajapalayam or Aruppukkottai. Add the drop and your mobile number, then tap Get Fare Estimate.',
      'Choose Sedan, SUV or Crysta and the leave date and time.',
      'Review the mapped fare and confirm through the existing booking system.',
    ],
    faqs: [
      {
        question: 'Is Sivakasi a separate district page?',
        answer:
          'No. Sivakasi is in Virudhunagar district. Set it as pickup or drop on /virudhunagar-taxi.',
      },
      {
        question: 'Which Tirunelveli page should a Virudhunagar traveller open?',
        answer:
          'Open a Tirunelveli route only when that existing Coimbatore or Chennai pair is the trip. A pickup in Virudhunagar starts on this district page. Both Tirunelveli spellings already on the site are left unchanged.',
      },
      {
        question: 'Can I book Virudhunagar to Madurai as one way?',
        answer:
          'Yes. Enter Madurai as the drop. That does not publish a new route URL, and it does not change /madurai-taxi.',
      },
      {
        question: 'Does this page print a Virudhunagar package?',
        answer:
          'No. The fare is calculated from the mapped drive and the same published one-way tariff used on the rest of the site.',
      },
    ],
    sampleDrops: ['Madurai', 'Sivakasi', 'Tirunelveli'],
    related: [
      { path: '/madurai-taxi', label: 'Madurai Taxi' },
      { path: '/coimbatore-to-thirunelveli-taxi', label: 'Coimbatore to Thirunelveli Taxi' },
      { path: '/chennai-to-tirunelveli-taxi', label: 'Chennai to Tirunelveli Taxi' },
      { path: '/tariff', label: 'Tariff' },
    ],
  },
];

const buildDistrictPage = (source) => ({
  ...source,
  slug: districtSlug(source.name),
  pickupName: source.name,
  h1: `${source.name} Taxi`,
  title: `${source.name} Taxi`,
  seoTitle: `${source.name} Taxi | One Way Cab | Easy Drop Taxi`,
  ctaTitle: `Ready to book a taxi from ${source.name}?`,
  ctaBody: `Enter the drop in the booking card and get a live fare estimate with EasyDropTaxi.`,
});

export const DISTRICT_LANDING_PAGES = SOURCE.map(buildDistrictPage);

const namesMatchAudit = () => {
  const built = DISTRICT_LANDING_PAGES.map((page) => page.name);
  return built.length === DISTRICT_AUDIT_NAMES.length
    && built.every((name, index) => name === DISTRICT_AUDIT_NAMES[index]);
};

if (!namesMatchAudit()) {
  throw new Error('District pages must use the 16 audit names in audit order.');
}

const seenSlugs = new Set();
DISTRICT_LANDING_PAGES.forEach((page) => {
  if (seenSlugs.has(page.slug)) {
    throw new Error(`Duplicate district slug ${page.slug}`);
  }
  seenSlugs.add(page.slug);
  if (RESERVED_PATHS.has(page.slug)) {
    throw new Error(`District slug collides with an existing URL: ${page.slug}`);
  }
  page.related.forEach((link) => {
    const allowed = RESERVED_PATHS.has(link.path)
      || CORE_PATHS.has(link.path)
      || seenSlugs.has(link.path)
      || DISTRICT_LANDING_PAGES.some((item) => item.slug === link.path);
    if (!allowed) {
      throw new Error(`Related link on ${page.name} is not an existing URL: ${link.path}`);
    }
  });
});
